/**
 * GUARD BUFFS (user rule, 2026-10-11): every art has exactly one buff move whose effect takes hold on a SUCCESSFUL BLOCK.
 * Pressing the move puts an "armed" status on the caster (`armed`, lasts its own duration); the first blow the caster then
 * blocks (outcome `blocked`) spends it and grants `grants` (a status with the real effect), plus optional rage/qi/heal.
 * Generic and data-driven: an art's guard buff is one row here and one `self: { status: armed }` on its buff skill
 * (systems/guardBuff.ts). Prototype: `tide-ward` (tt-ward of the staff art). The other arts' buffs still act at once.
 */
export interface GuardBuffDef {
  /** The status the buff move puts on the caster (waiting for a block). */
  readonly armed: string;
  /** The status granted when a blow is blocked while armed. */
  readonly grants: string;
  /** Rage granted with it (0-100 bar), if any. */
  readonly rage?: number;
  /** Qi given back, if any. */
  readonly qi?: number;
  /** Fraction of max HP healed at once, if any. */
  readonly healPct?: number;
}

export const GUARD_BUFFS: Readonly<Record<string, GuardBuffDef>> = {
  'tide-ward': { armed: 'tide-ward', grants: 'tide-surge' },
  // E2a: first six arts of data/arts.ts (one distinct on-block effect each)
  'vow-guard': { armed: 'vow-guard', grants: 'ward', healPct: 0.05 }, // hum-palm: calm shell + heal
  'bell-guard': { armed: 'bell-guard', grants: 'iron-bell', rage: 10 }, // founder-sabre: bell rings, rage
  'decree-guard': { armed: 'decree-guard', grants: 'war-cry', rage: 5 }, // chain-hold: punish, +25% damage
  'loyal-guard': { armed: 'loyal-guard', grants: 'haste', qi: 10 }, // quick-draw-sabre: speed + qi
  'mirror-guard': { armed: 'mirror-guard', grants: 'moon-edge' }, // listening-blade: crit window
  'trance-guard': { armed: 'trance-guard', grants: 'trance' }, // chorus: trance (dmg + speed)
  // E2b: arts 7-12 (distinct on-block effect each)
  'pulse-guard': { armed: 'pulse-guard', grants: 'pulse-flow', qi: 20 }, // pulse-seal: flowing step + qi
  'screen-guard': { armed: 'screen-guard', grants: 'still-mind', qi: 8 }, // one-cut: stillness (heavier cut, slower feet) + qi
  'fury-guard': { armed: 'fury-guard', grants: 'blood-frenzy', rage: 15 }, // tongs-claw: frenzy + rage
  'cliff-guard': { armed: 'cliff-guard', grants: 'cliff-stand', healPct: 0.03 }, // cliff-push-sabre: stand firm, hit back
  'scale-guard': { armed: 'scale-guard', grants: 'dragon-scale' }, // dragon-palm: scales (less damage + lifesteal)
  'smoke-guard': { armed: 'smoke-guard', grants: 'smoke-shadow' }, // hidden-arts: vanish in smoke (speed + crit)
  // E2c (TODO-polish: placeholder effects, ns-balance to tune): remaining arts
  'nine-guard': { armed: 'nine-guard', grants: 'nine-resolve', rage: 10 }, // nine-swords
  'cloud-guard': { armed: 'cloud-guard', grants: 'cloud-step', qi: 10 }, // cloud-palm
  'breeze-guard': { armed: 'breeze-guard', grants: 'breeze-veil' }, // breeze-sword
  'yield-guard': { armed: 'yield-guard', grants: 'taiji-yield', healPct: 0.03 }, // drift-fist
  'focus-guard': { armed: 'focus-guard', grants: 'focus-calm', qi: 10 }, // drift-sword
  'frost-guard': { armed: 'frost-guard', grants: 'frost-veil' }, // snow-sword
  'arhat-guard': { armed: 'arhat-guard', grants: 'arhat-bell', rage: 8 }, // bell-fist
  'brocade-guard': { armed: 'brocade-guard', grants: 'brocade-oath' }, // lamp-hook-sabre
  'bliss-guard': { armed: 'bliss-guard', grants: 'bliss-step' }, // quiet-palm
  'brush-guard': { armed: 'brush-guard', grants: 'brush-ink' }, // ink-slate
  'sabre-guard': { armed: 'sabre-guard', grants: 'sabre-rage', rage: 15 }, // heat-sabre
  'golden-guard': { armed: 'golden-guard', grants: 'golden-roar', rage: 10 }, // golden-dragon-palm
};
